import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Award, BookOpen, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", photo: "" });
  const [enrollments, setEnrollments] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", photo: user.photo || "" });
  }, [user]);
  useEffect(() => {
    api.get("/my/enrollments").then((r) => setEnrollments(r.data));
    api.get("/my/certificates").then((r) => setCerts(r.data));
  }, []);

  async function save() {
    await api.put("/me", form);
    await refresh();
    toast.success("Profile updated");
  }

  if (!user) return null;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="profile-page">
      <div className="rounded-3xl overflow-hidden royal-gradient text-white p-8 relative grain">
        <div className="absolute -bottom-10 -right-10 h-60 w-60 rounded-full bg-gold/40 blur-3xl" />
        <div className="relative flex items-center gap-5">
          <div className="h-20 w-20 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-3xl font-display font-bold">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{user.name}</h1>
            <p className="text-white/80 mt-1">{user.email}</p>
            <p className="mt-2 text-xs uppercase tracking-widest text-gold">{user.role}</p>
          </div>
        </div>
        <div className="relative mt-6 grid grid-cols-3 gap-4 text-center">
          <div><p className="font-display text-2xl font-bold">{enrollments.length}</p><p className="text-xs text-white/70">Courses</p></div>
          <div><p className="font-display text-2xl font-bold">{certs.length}</p><p className="text-xs text-white/70">Certificates</p></div>
          <div><p className="font-display text-2xl font-bold">{user.points}</p><p className="text-xs text-white/70">Points</p></div>
        </div>
      </div>

      <div className="rounded-3xl bg-card border border-border p-6">
        <h2 className="font-display text-xl font-bold mb-4">Edit profile</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Full name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="profile-name" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl h-11" data-testid="profile-phone" />
          </div>
          <div className="sm:col-span-2">
            <Label>Photo URL</Label>
            <Input value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} className="mt-1.5 rounded-xl h-11" placeholder="https://..." />
          </div>
        </div>
        <Button className="mt-5 rounded-full royal-gradient border-0 text-white" onClick={save} data-testid="save-profile">Save changes</Button>
      </div>

      <div className="rounded-3xl bg-card border border-border p-6">
        <h2 className="font-display text-xl font-bold mb-4">My learning</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {enrollments.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border p-4 flex items-center gap-3">
              <img src={e.course.thumbnail} alt="" className="h-14 w-14 rounded-xl object-cover" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{e.course.title}</p>
                <p className="text-xs text-muted-foreground">{e.progress}% complete</p>
              </div>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </div>
          ))}
          {enrollments.length === 0 && <p className="text-sm text-muted-foreground">Enroll in a course to see it here.</p>}
        </div>
      </div>
    </div>
  );
}
